import { Divider, Stack } from '@mui/material'
import { ArrowUpRightIcon, BlueprintIcon, CalendarDotIcon } from '@phosphor-icons/react'
import {
    AppraiserAssignment,
    isoDateStringToLocalizedMonthYear,
    isoDateStringToLocalFormat,
    joinDefinedStrings,
    MonetaryValue,
    TinyNavButton,
    tokens,
    type User,
} from 'ComponentLibrary'
import type { AppraiserAssignment as AppraiserAssignmentType } from 'ComponentLibrary/src/common/users'
import type { MonetaryValueDataObjectType } from 'ComponentLibrary/src/common/money'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'

type ValueCheckBaseValuationWidgetProps = {
    valuationDate?: string
    valuationType?: 'einwert' | 'external' | 'historical' | string
    appraiserAssignment?: AppraiserAssignmentType
    appraisingEntityName?: string
    marketValue: MonetaryValueDataObjectType
    mortgageLendingValue?: MonetaryValueDataObjectType | null
    baseValuationUrl?: string
    baseValuationAppraiserUsers: User[]
    copy?: ValueCheckBaseValuationWidgetCopy
    locale?: string
}

export type ValueCheckBaseValuationWidgetCopy = {
    title: string
    valuationLabel: string
    marketValueLabel: string
    mortgageLendingValueLabel: string
    actionLabel: string
}

const DEFAULT_VALUE_CHECK_BASE_VALUATION_WIDGET_COPY: ValueCheckBaseValuationWidgetCopy = {
    title: 'Zugrundeliegendes Gutachten',
    valuationLabel: 'Wertgutachten',
    marketValueLabel: 'Marktwert',
    mortgageLendingValueLabel: 'Beleihungswert',
    actionLabel: 'Zur Bewertung',
}

export function ValueCheckBaseValuationWidget({
    valuationDate,
    valuationType,
    appraiserAssignment,
    appraisingEntityName,
    marketValue,
    mortgageLendingValue,
    baseValuationUrl,
    baseValuationAppraiserUsers,
    copy = DEFAULT_VALUE_CHECK_BASE_VALUATION_WIDGET_COPY,
    locale = 'de-DE',
}: ValueCheckBaseValuationWidgetProps) {
    const formattedValuationDate = isoDateStringToLocalFormat(valuationDate, false, locale)
    const valuationMonthAndYear = isoDateStringToLocalizedMonthYear(valuationDate, 'long', locale)
    const valuationTitle = joinDefinedStrings([copy.valuationLabel, valuationMonthAndYear])
    const marketValueText = MonetaryValue.fromMonetaryValueDataObject(marketValue).format({ withoutDecimals: true, locale })
    const mortgageLendingValueText = mortgageLendingValue
        ? MonetaryValue.fromMonetaryValueDataObject(mortgageLendingValue).format({ withoutDecimals: true, locale })
        : undefined

    return (
        <Stack direction="column" gap={tokens.SizingSpacing200}>
            <Headline title={copy.title} fullWidth variant="headline3" />
            <Stack
                direction="column"
                gap={tokens.SizingSpacing050}
                padding={tokens.SizingSpacing150}
                bgcolor={tokens.ColorSurfaceInverseDefault}
                borderRadius={tokens.SizingCornerS}
            >
                <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                    {valuationTitle}
                </Typography>
                <Stack direction="row" gap={tokens.SizingSpacing100} height="22px" alignItems="center">
                    <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                        <CalendarDotIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} />
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {formattedValuationDate}
                        </Typography>
                    </Stack>
                    {appraiserAssignment && (
                        <AppraiserAssignment
                            appraiserAssignment={appraiserAssignment}
                            externalAppraisingEntityName={valuationType !== 'einwert' ? appraisingEntityName : undefined}
                            users={baseValuationAppraiserUsers}
                            size="small"
                        />
                    )}
                </Stack>
                <Divider sx={{ borderColor: tokens.ColorBorderDefault }} />
                <Stack direction="row" gap={tokens.SizingSpacing150} justifyContent="space-between" height="44px" alignItems="center">
                    <Stack direction="column">
                        <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                            {marketValueText}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {copy.marketValueLabel}
                        </Typography>
                    </Stack>
                    {mortgageLendingValueText && (
                        <Stack direction="column" justifyContent="flex-end">
                            <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                                {mortgageLendingValueText}
                            </Typography>
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {copy.mortgageLendingValueLabel}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            {baseValuationUrl && valuationType !== 'historical' && (
                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <TinyNavButton
                        leftIcon={<BlueprintIcon />}
                        rightIcon={<ArrowUpRightIcon />}
                        label={copy.actionLabel}
                        href={baseValuationUrl}
                    />
                </Stack>
            )}
        </Stack>
    )
}
